import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography } from '@mui/material';
import { StorefrontOutlined } from '@mui/icons-material';
import { GridComponent, ColumnsDirective, ColumnDirective, Page, Sort, Inject, SortDirection, RowSelectEventArgs } from '@syncfusion/ej2-react-grids';
import { VendorWorklistView } from '../../../../services/VendorService';
import { LoadingIndicator, SectionPaper } from '../../../common/components/styled-ui-elements';
import { useGetActiveVendorsQuery } from '../../../../services/VendorService';
import { VendorEmptyState } from '../shared/states';

interface VendorsGridProps {
  searchText?: string;
  showInactive?: boolean;
}

export const VendorsGrid: React.FC<VendorsGridProps> = ({ 
  searchText = '',
  showInactive = false
}) => {
  const navigate = useNavigate();
  const { data: vendors } = useGetActiveVendorsQuery();

  const filteredVendors = React.useMemo(() => {
    if (!vendors) return [];
    
    let filtered = [...vendors];

    if (!showInactive) {
      filtered = filtered.filter(vendor => vendor.status === 'ACTV');
    }

    if (searchText.trim() !== '') {
      const search = searchText.toLowerCase();
      filtered = filtered.filter(vendor =>
        vendor.companyName.toLowerCase().includes(search) ||
        vendor.vendorNumber.toLowerCase().includes(search) ||
        vendor.dba.toLowerCase().includes(search) ||
        vendor.primaryContactName.toLowerCase().includes(search) ||
        vendor.primaryEmail.toLowerCase().includes(search) ||
        vendor.statusName.toLowerCase().includes(search)
      );
    }

    return filtered;
  }, [vendors, searchText, showInactive]);

  const getStatusColor = (status: string): { bg: string; color: string } => {
    switch (status) {
      case 'ACTV': return { bg: '#d1fae5', color: '#065f46' };
      case 'INAC': return { bg: '#fee2e2', color: '#991b1b' };
      case 'SUSP': return { bg: '#fef3c7', color: '#92400e' };
      case 'PEND': return { bg: '#dbeafe', color: '#1e40af' };
      default: return { bg: '#e5e7eb', color: '#374151' };
    }
  };

  const statusTemplate = (vendor: VendorWorklistView) => {
    const colors = getStatusColor(vendor.status);
    return (
      <span
        style={{
          display: 'inline-block',
          backgroundColor: colors.bg,
          color: colors.color,
          fontWeight: 600,
          fontSize: '0.7rem',
          padding: '2px 10px',
          borderRadius: '12px',
          lineHeight: '18px',
          whiteSpace: 'nowrap',
        }}
      >
        {vendor.statusName}
      </span>
    );
  };

  const handleRowSelected = (args: RowSelectEventArgs) => {
    const data = args?.data as { id?: string };
    if (data?.id) {
      navigate(`/vendors/edit?id=${data.id}`);
    }
  };

  if (!vendors) {
    return (
      <SectionPaper>
        <LoadingIndicator icon={StorefrontOutlined} message="Loading vendors..." />
      </SectionPaper>
    );
  }

  if (filteredVendors.length === 0) {
    return (
      <VendorEmptyState
        message={searchText ? 'No vendors found. Try adjusting your search.' : 'No vendors found. Create your first vendor to get started.'}
      />
    );
  }

  return (
    <SectionPaper>
      <Typography 
        variant="caption" 
        sx={{ 
          mb: 1, 
          display: 'block',
          color: '#64748b',
          fontWeight: 500
        }}
      >
        Showing {filteredVendors.length} vendor{filteredVendors.length !== 1 ? 's' : ''}
      </Typography>
      <GridComponent
        dataSource={filteredVendors}
        allowPaging={true}
        allowSorting={true}
        pageSettings={{ pageSize: 20 }}
        sortSettings={{
          columns: [{ field: 'companyName', direction: 'Ascending' as SortDirection }]
        }}
        height={500}
        rowHeight={36}
        gridLines="Both"
        rowSelected={handleRowSelected}
        cssClass="clickable-rows"
      >
        <ColumnsDirective>
          <ColumnDirective field="vendorNumber" headerText="Vendor #" width="100" textAlign="Left" />
          <ColumnDirective field="companyName" headerText="Company Name" width="180" textAlign="Left" />
          <ColumnDirective field="dba" headerText="DBA" width="140" textAlign="Left" />
          <ColumnDirective headerText="Status" width="100" textAlign="Center" template={statusTemplate} />
          <ColumnDirective field="primaryContactName" headerText="Primary Contact" width="150" textAlign="Left" />
          <ColumnDirective field="primaryPhone" headerText="Phone" width="120" textAlign="Left" />
          <ColumnDirective field="primaryEmail" headerText="Email" width="180" textAlign="Left" />
        </ColumnsDirective>
        <Inject services={[Page, Sort]} />
      </GridComponent>
    </SectionPaper>
  );
};

export default VendorsGrid;
